/**
 * DayDetailModal - Shows all workouts for a selected day
 * Mobile-first responsive design with inline editing
 * Shows set-by-set comparison with previous workout
 *
 * Refactored: Touch-friendly, useBodyScrollLock, ESC key support
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X, Dumbbell, Plus, Trash2, Check, Loader2, Pencil, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useFitnessStore } from '../../stores/fitnessStore';
import { fitness } from '../../services/supabaseService';
import { coachRelationships } from '../../services/coachService';
import toast from 'react-hot-toast';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const DayDetailModal = () => {
  const {
    isDayDetailModalOpen,
    selectedDate,
    selectedDayWorkouts,
    selectedDayClientId,
    closeDayDetailModal,
    openAddWorkoutModal,
    updateWorkout,
    deleteWorkout,
  } = useFitnessStore();

  // Inline edit state
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({ reps: '', weight: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Comparison data: { exerciseName: { date: string, sets: [{weight, reps}] } }
  const [comparisonData, setComparisonData] = useState({});
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);

  // Body scroll lock (iOS-safe)
  useBodyScrollLock(isDayDetailModalOpen);

  // ESC key to close
  useEffect(() => {
    if (!isDayDetailModalOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (editingRow) {
          cancelEdit();
        } else {
          closeDayDetailModal();
        }
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isDayDetailModalOpen, editingRow, closeDayDetailModal]);

  // Fetch comparison data when modal opens
  useEffect(() => {
    if (!isDayDetailModalOpen || !selectedDate || !selectedDayWorkouts?.length) {
      setComparisonData({});
      return;
    }

    const fetchComparisonData = async () => {
      setIsLoadingComparison(true);
      const comparison = {};

      // Get unique exercise names
      const exerciseNames = [...new Set(selectedDayWorkouts.map(w => w.exercise_name))];

      console.log('[DayDetailModal] Fetching comparison data for:', {
        exerciseNames,
        selectedDate,
        selectedDayClientId,
      });

      try {
        if (selectedDayClientId) {
          // Coach mode: Fetch client's previous workouts via coach service
          // Get all client workouts before the selected date
          const clientWorkouts = await coachRelationships.getClientWorkouts(
            selectedDayClientId,
            null, // no start date limit
            selectedDate // up to (but not including) this date
          );

          // Filter for workouts before selectedDate
          const previousWorkouts = clientWorkouts.filter(w => w.date < selectedDate);

          // Group by exercise and find the most recent for each
          exerciseNames.forEach(exerciseName => {
            const exerciseWorkouts = previousWorkouts
              .filter(w => w.exercise_name === exerciseName)
              .sort((a, b) => {
                // Sort by date desc, then by row_order
                if (a.date !== b.date) return b.date.localeCompare(a.date);
                return (a.row_order || 0) - (b.row_order || 0);
              });

            if (exerciseWorkouts.length > 0) {
              // Get the most recent date
              const lastDate = exerciseWorkouts[0].date;
              // Get all sets from that date
              const lastDateSets = exerciseWorkouts
                .filter(w => w.date === lastDate)
                .sort((a, b) => (a.row_order || 0) - (b.row_order || 0));

              comparison[exerciseName] = {
                date: lastDate,
                sets: lastDateSets.map(s => ({
                  weight: parseFloat(s.weight) || 0,
                  reps: s.reps || 0,
                })),
              };
            }
          });
        } else {
          // Normal user mode: Fetch own previous workouts
          await Promise.all(
            exerciseNames.map(async (exerciseName) => {
              try {
                const previousSets = await fitness.getPreviousWorkoutSets(
                  exerciseName,
                  selectedDate,
                  null
                );

                if (previousSets.length > 0) {
                  comparison[exerciseName] = {
                    date: previousSets[0].date,
                    sets: previousSets.map(s => ({
                      weight: parseFloat(s.weight) || 0,
                      reps: s.reps || 0,
                    })),
                  };
                }
              } catch (err) {
                console.error(`Error fetching comparison for ${exerciseName}:`, err);
              }
            })
          );
        }
      } catch (err) {
        console.error('[DayDetailModal] Error fetching comparison data:', err);
      }

      console.log('[DayDetailModal] Final comparison data:', comparison);
      setComparisonData(comparison);
      setIsLoadingComparison(false);
    };

    fetchComparisonData();
  }, [isDayDetailModalOpen, selectedDate, selectedDayWorkouts, selectedDayClientId]);

  // Group workouts by exercise_name, expanding reps_detail for legacy records
  const groupedWorkouts = useMemo(() => {
    if (!selectedDayWorkouts?.length) return [];

    // Expand legacy records that have reps_detail into individual set records
    const expandedWorkouts = [];
    selectedDayWorkouts.forEach((workout) => {
      // Check if this is a legacy record with reps_detail (multiple sets in one record)
      if (workout.reps_detail && Array.isArray(workout.reps_detail) && workout.reps_detail.length > 1) {
        // Expand each set in reps_detail into a virtual workout record
        workout.reps_detail.forEach((setDetail, idx) => {
          expandedWorkouts.push({
            ...workout,
            id: `${workout.id}_set_${idx}`, // Virtual ID for UI
            _originalId: workout.id, // Keep original for editing
            _setIndex: idx,
            reps: setDetail.reps,
            weight: setDetail.weight,
            row_order: (workout.row_order || 0) * 100 + idx, // Preserve order
            notes: idx === 0 ? workout.notes : null, // Only show notes on first set
          });
        });
      } else {
        // New format or single set - use as-is
        expandedWorkouts.push(workout);
      }
    });

    // Sort by row_order (preserves import/insertion order)
    const sorted = expandedWorkouts.sort((a, b) => {
      return (a.row_order || 0) - (b.row_order || 0);
    });

    // Group by exercise_name, preserving order of first appearance
    const groups = [];
    const groupMap = new Map();

    sorted.forEach((workout) => {
      const name = workout.exercise_name || 'Diğer';
      if (!groupMap.has(name)) {
        const group = {
          exerciseName: name,
          muscleGroup: workout.muscle_group,
          sets: [],
        };
        groupMap.set(name, group);
        groups.push(group);
      }
      const group = groupMap.get(name);
      group.sets.push(workout);
    });

    return groups;
  }, [selectedDayWorkouts]);

  // Flatten for total counts
  const allWorkouts = useMemo(() => {
    return groupedWorkouts.flatMap(g => g.sets);
  }, [groupedWorkouts]);

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const date = parseISO(dateStr);
    return format(date, 'd MMMM yyyy, EEEE', { locale: tr });
  };

  // Start inline edit
  const startEdit = useCallback((workout) => {
    setEditingRow(workout.id);
    if (workout.exercise_type === 'cardio') {
      setEditValues({
        reps: workout.duration_minutes?.toString() || '',
        weight: workout.distance_km?.toString() || '',
      });
    } else {
      setEditValues({
        reps: workout.reps?.toString() || '',
        weight: workout.weight?.toString() || '',
      });
    }
  }, []);

  // Cancel inline edit
  const cancelEdit = useCallback(() => {
    setEditingRow(null);
    setEditValues({ reps: '', weight: '' });
  }, []);

  // Save inline edit
  const saveEdit = useCallback(async (workoutId, workout) => {
    if (isSaving) return;

    // Handle virtual IDs from expanded legacy records
    const isVirtualId = typeof workoutId === 'string' && workoutId.includes('_set_');
    const actualId = isVirtualId ? workout._originalId : workoutId;
    const setIndex = isVirtualId ? workout._setIndex : null;

    const isCardio = workout?.exercise_type === 'cardio';

    setIsSaving(true);
    try {
      let updateData;

      if (isCardio) {
        const duration_minutes = parseInt(editValues.reps) || 0;
        const distance_km = parseFloat(editValues.weight) || 0;
        updateData = { duration_minutes, distance_km };
      } else {
        const reps = parseInt(editValues.reps) || 0;
        const weight = parseFloat(editValues.weight) || 0;
        if (reps <= 0) {
          toast.error('Tekrar sayısı gerekli');
          setIsSaving(false);
          return;
        }

        if (isVirtualId && setIndex !== null) {
          // Legacy record: update reps_detail array
          const originalWorkout = selectedDayWorkouts.find(w => w.id === actualId);
          if (originalWorkout?.reps_detail) {
            const newRepsDetail = [...originalWorkout.reps_detail];
            newRepsDetail[setIndex] = { ...newRepsDetail[setIndex], reps, weight };
            updateData = { reps_detail: newRepsDetail };
          } else {
            updateData = { reps, weight };
          }
        } else {
          // New format: direct update
          updateData = { reps, weight };
        }
      }

      // Use store's updateWorkout - handles coach mode automatically
      await updateWorkout(actualId, updateData);

      // Reload workouts to reflect changes
      closeDayDetailModal();
      toast.success('Set güncellendi');
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
      setEditingRow(null);
      setEditValues({ reps: '', weight: '' });
    }
  }, [editValues, isSaving, updateWorkout, selectedDayWorkouts, closeDayDetailModal]);

  // Handle delete workout
  const handleDelete = async (workoutId, workout) => {
    // Handle virtual IDs from expanded legacy records
    const isVirtualId = typeof workoutId === 'string' && workoutId.includes('_set_');
    const actualId = isVirtualId ? workout?._originalId : workoutId;
    const setIndex = isVirtualId ? workout?._setIndex : null;

    if (isVirtualId && setIndex !== null) {
      // Legacy record with multiple sets - ask if they want to delete just this set or all
      if (!confirm('Bu seti silmek istediğinize emin misiniz?')) return;

      try {
        const originalWorkout = selectedDayWorkouts.find(w => w.id === actualId);
        if (originalWorkout?.reps_detail && originalWorkout.reps_detail.length > 1) {
          // Remove just this set from reps_detail
          const newRepsDetail = originalWorkout.reps_detail.filter((_, idx) => idx !== setIndex);
          await updateWorkout(actualId, { reps_detail: newRepsDetail });
        } else {
          // Only one set left, delete entire record
          await deleteWorkout(actualId);
        }
        closeDayDetailModal();
        toast.success('Set silindi');
      } catch (error) {
        console.error('Delete error:', error);
      }
    } else {
      // New format - direct delete
      if (!confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;

      try {
        await deleteWorkout(actualId);
        const updatedWorkouts = selectedDayWorkouts.filter(w => w.id !== actualId);
        if (updatedWorkouts.length === 0) {
          closeDayDetailModal();
        } else {
          useFitnessStore.setState({ selectedDayWorkouts: updatedWorkouts });
        }
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  // Get muscle group label
  const getMuscleGroupLabel = (group) => {
    const labels = {
      chest: 'Göğüs',
      back: 'Sırt',
      shoulders: 'Omuz',
      biceps: 'Biceps',
      triceps: 'Triceps',
      quadriceps: 'Ön Bacak',
      hamstrings: 'Arka Bacak',
      glutes: 'Kalça',
      calves: 'Baldır',
      abdominals: 'Karın',
      arms: 'Kol',
      legs: 'Bacak',
      core: 'Karın',
      full_body: 'Tüm Vücut',
    };
    return labels[group] || group;
  };

  // Get comparison info for a specific set
  const getSetComparison = useCallback((exerciseName, setIndex, currentWeight) => {
    const prevData = comparisonData[exerciseName];
    if (!prevData || !prevData.sets[setIndex]) {
      // No previous data for this set
      return { type: 'new', diff: 0, prevWeight: null };
    }

    const prevWeight = prevData.sets[setIndex].weight;
    const diff = currentWeight - prevWeight;

    if (diff > 0) {
      return { type: 'up', diff, prevWeight };
    } else if (diff < 0) {
      return { type: 'down', diff, prevWeight };
    } else {
      return { type: 'same', diff: 0, prevWeight };
    }
  }, [comparisonData]);

  // Render comparison indicator
  const renderComparisonBadge = (comparison) => {
    if (!comparison || comparison.type === 'new') {
      return null;
    }

    const { type, diff } = comparison;

    if (type === 'up') {
      return (
        <span className="inline-flex items-center gap-0.5 text-green-400 text-xs font-medium ml-1">
          <TrendingUp className="w-3 h-3" />
          <span>+{diff.toFixed(1)}</span>
        </span>
      );
    } else if (type === 'down') {
      return (
        <span className="inline-flex items-center gap-0.5 text-red-400 text-xs font-medium ml-1">
          <TrendingDown className="w-3 h-3" />
          <span>{diff.toFixed(1)}</span>
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-0.5 text-gray-500 text-xs ml-1">
          <Minus className="w-3 h-3" />
        </span>
      );
    }
  };

  if (!isDayDetailModalOpen) return null;

  const totalSets = allWorkouts.length;
  const totalExercises = groupedWorkouts.length;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && closeDayDetailModal()}
    >
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full sm:max-w-2xl lg:max-w-4xl max-h-[95vh] sm:max-h-[85vh] bg-gray-900 border-t sm:border border-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col sm:m-4"
      >
        {/* Mobile drag indicator */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-700 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 sm:p-4 border-b border-gray-800">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-orange-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Dumbbell className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm sm:text-lg font-semibold text-white truncate">
                {formatDisplayDate(selectedDate)}
              </h2>
              <p className="text-xs sm:text-sm text-gray-400">
                {totalExercises} hareket • {totalSets} set
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => {
                closeDayDetailModal();
                openAddWorkoutModal(selectedDate);
              }}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 bg-orange-600 hover:bg-orange-500 active:bg-orange-700 text-white rounded-lg text-sm font-medium transition-colors min-h-touch"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden xs:inline">Ekle</span>
            </button>
            <button
              onClick={closeDayDetailModal}
              className="p-2.5 text-gray-400 hover:text-white active:bg-gray-800 hover:bg-gray-800 rounded-lg transition-colors min-h-touch min-w-touch flex items-center justify-center"
              aria-label="Kapat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - Grouped by exercise */}
        <div className="flex-1 overflow-auto">
          {groupedWorkouts.length === 0 ? (
            <div className="text-center py-12">
              <Dumbbell className="w-10 h-10 sm:w-12 sm:h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm sm:text-base">Bu gün için kayıt yok</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {groupedWorkouts.map((group) => (
                <div key={group.exerciseName} className="py-3">
                  {/* Exercise Header */}
                  <div className="px-4 pb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-white">{group.exerciseName}</h3>
                      {group.muscleGroup && (
                        <span className="px-1.5 py-0.5 bg-gray-700/50 text-gray-400 rounded text-2xs">
                          {getMuscleGroupLabel(group.muscleGroup)}
                        </span>
                      )}
                      {/* Previous workout date indicator */}
                      {comparisonData[group.exerciseName] && (
                        <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded text-2xs">
                          vs {format(parseISO(comparisonData[group.exerciseName].date), 'd MMM', { locale: tr })}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {group.sets[0]?.exercise_type === 'cardio'
                        ? `${group.sets.reduce((sum, w) => sum + (parseInt(w.duration_minutes) || 0), 0)} dk`
                        : `${group.sets.length} set`
                      }
                    </div>
                  </div>

                  {/* Sets list */}
                  <div className="space-y-1">
                    {group.sets.map((workout, setIdx) => {
                      const isEditing = editingRow === workout.id;

                      return (
                        <div
                          key={workout.id}
                          className={`mx-2 px-3 py-2 rounded-lg ${isEditing ? 'bg-gray-800' : 'bg-gray-800/30 hover:bg-gray-800/50'} transition-colors`}
                        >
                          <div className="flex items-center gap-3">
                            {/* Set number */}
                            <div className="w-6 h-6 bg-gray-700 rounded-md flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-medium text-gray-400">{setIdx + 1}</span>
                            </div>

                            {/* Set data */}
                            <div className="flex-1 min-w-0">
                              {isEditing ? (
                                <div className="flex items-center gap-2 flex-wrap">
                                  {workout.exercise_type === 'cardio' ? (
                                    <>
                                      <div className="flex items-center gap-1.5">
                                        <input
                                          type="number"
                                          inputMode="numeric"
                                          value={editValues.reps}
                                          onChange={(e) => setEditValues(prev => ({ ...prev, reps: e.target.value }))}
                                          placeholder="30"
                                          className="w-16 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-sm font-medium focus:outline-none focus:border-orange-500"
                                          aria-label="Süre"
                                        />
                                        <span className="text-gray-500 text-xs">dk</span>
                                      </div>
                                      <span className="text-gray-600 font-bold">•</span>
                                      <div className="flex items-center gap-1.5">
                                        <input
                                          type="number"
                                          inputMode="decimal"
                                          step="0.5"
                                          value={editValues.weight}
                                          onChange={(e) => setEditValues(prev => ({ ...prev, weight: e.target.value }))}
                                          placeholder="5.0"
                                          className="w-20 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-orange-400 text-center text-sm font-medium focus:outline-none focus:border-orange-500"
                                          aria-label="Mesafe"
                                        />
                                        <span className="text-gray-500 text-xs">km</span>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="flex items-center gap-1.5">
                                        <input
                                          type="number"
                                          inputMode="numeric"
                                          value={editValues.reps}
                                          onChange={(e) => setEditValues(prev => ({ ...prev, reps: e.target.value }))}
                                          placeholder="12"
                                          className="w-16 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-sm font-medium focus:outline-none focus:border-orange-500"
                                          aria-label="Tekrar sayısı"
                                        />
                                        <span className="text-gray-500 text-xs">tekrar</span>
                                      </div>
                                      <span className="text-gray-600 font-bold">×</span>
                                      <div className="flex items-center gap-1.5">
                                        <input
                                          type="number"
                                          inputMode="decimal"
                                          step="0.5"
                                          value={editValues.weight}
                                          onChange={(e) => setEditValues(prev => ({ ...prev, weight: e.target.value }))}
                                          placeholder="50"
                                          className="w-20 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-orange-400 text-center text-sm font-medium focus:outline-none focus:border-orange-500"
                                          aria-label="Ağırlık"
                                        />
                                        <span className="text-gray-500 text-xs">kg</span>
                                      </div>
                                    </>
                                  )}
                                  <div className="flex items-center gap-1.5 ml-auto">
                                    <button
                                      onClick={() => saveEdit(workout.id, workout)}
                                      disabled={isSaving}
                                      className="p-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors disabled:opacity-50"
                                      aria-label="Kaydet"
                                    >
                                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                    </button>
                                    <button
                                      onClick={cancelEdit}
                                      className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
                                      aria-label="İptal"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  {workout.exercise_type === 'cardio' ? (
                                    <>
                                      <span className="font-semibold text-white">{workout.duration_minutes || '-'}</span>
                                      <span className="text-gray-500 text-sm">dk</span>
                                      <span className="text-gray-600">•</span>
                                      <span className="font-semibold text-orange-400">
                                        {workout.distance_km ? parseFloat(workout.distance_km).toFixed(1) : '-'}
                                      </span>
                                      <span className="text-gray-500 text-sm">km</span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="font-semibold text-white">{workout.reps || '-'}</span>
                                      <span className="text-gray-500 text-sm">tekrar</span>
                                      <span className="text-gray-600">×</span>
                                      <span className="font-semibold text-orange-400">
                                        {workout.weight ? parseFloat(workout.weight).toFixed(1) : '-'}
                                      </span>
                                      <span className="text-gray-500 text-sm">kg</span>
                                      {/* Comparison badge */}
                                      {!isLoadingComparison && renderComparisonBadge(
                                        getSetComparison(group.exerciseName, setIdx, parseFloat(workout.weight) || 0)
                                      )}
                                    </>
                                  )}
                                  <button
                                    onClick={() => startEdit(workout)}
                                    className="p-1.5 text-gray-500 hover:text-blue-400 hover:bg-blue-500/10 rounded-md transition-colors ml-1"
                                    title="Düzenle"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                              {/* Notes - shown below the set data */}
                              {workout.notes && !workout.notes.match(/^Set \d+$/) && (
                                <p className="text-xs text-gray-500 mt-1 italic">
                                  {workout.notes.replace(/^Set \d+ - /, '')}
                                </p>
                              )}
                            </div>

                            {/* Delete button */}
                            {!isEditing && (
                              <button
                                onClick={() => handleDelete(workout.id, workout)}
                                className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors flex-shrink-0"
                                aria-label="Sil"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

{/* Footer removed - sets shown individually above */}
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default DayDetailModal;
